import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"
import type {DataObject} from "@cm/utils/data-object"
import type {TypedImageData} from "@cm/utils"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"

const TD = TypeDescriptors

type LoadImageDataDescriptor = {
    sceneManager: InletDescriptor<ISceneManager>
    dataObject: InletDescriptor<DataObject>
    imageData: OutletDescriptor<TypedImageData>
}

const loadImageDataDescriptor: LoadImageDataDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    dataObject: TD.inlet(TD.DataObject),
    imageData: TD.outlet(TD.Identity<TypedImageData>()),
}

export class LoadImageData implements NodeClassImpl<LoadImageDataDescriptor, typeof LoadImageData> {
    static descriptor = loadImageDataDescriptor
    static uniqueName = "LoadImageData"
    sceneManager!: Inlet<ISceneManager>
    dataObject!: Inlet<DataObject>
    imageData!: Outlet<TypedImageData>

    private pending: Subscription | null = null

    run() {
        this.cleanup()

        if (this.sceneManager === NotReady || this.dataObject === NotReady) {
            this.imageData.emitIfChanged(NotReady)
            return
        }

        const {sceneManager, dataObject} = this

        this.pending = sceneManager.addTask({
            description: `loadImageData(${dataObject.legacyId})`,
            task: defer(() => sceneManager.getDataObjectImageData(dataObject)).pipe(
                map((imageData) => {
                    this.imageData.emitIfChanged(imageData)
                }),
            ),
            critical: true,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
